import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/fb';

// REDUX
import { setCurrentPage, setSentTokenMessage, unloadUnityApp, setTriedToReload, setPrivacy, setAppVerbosity, setAppReactVerbosity } from '../redux/app.slice';
import { setToken, setIsRefreshing, clearToken, setExpiresAt } from '../redux/token.slice';
import { setIdToken, setLoginStatus, resetUser, setUser, setName, setRole, setOrganization } from '../redux/user.slice';

// APIs - import reloadSession and fetchAppVerbosityApi
import { reloadSession } from '../apis/user.api';
import { fetchAppVerbosityApi } from '../apis/app.api';

export const useRefreshToken = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [idTokenResultSave, setIdTokenResultSave] = useState(null);
  const { idToken, expiresAt, isRefreshing } = useSelector((state) => state.token);
  const { user } = useSelector((state) => state.user);
  const { reactVerbosity, triedToReload } = useSelector((state) => state.app);
  
  reactVerbosity === 5 && console.log('$$$ ---> useTokenManager: useRefreshToken user:', user)
           
  useEffect(() => {
    if (idTokenResultSave != null) {
        // console.log('$$$$ --- useTokenManager: checkTokenExpiration idTokenResultSave VS idToken:', idTokenResultSave.token != idToken ? 'DIFFERENT TOKEN' : 'SAME TOKEN')
        // console.log('$$$$ --- useTokenManager: checkTokenExpiration idTokenResultSave VS user.idToken:', idTokenResultSave.token != user.idToken ? 'DIFFERENT TOKEN' : 'SAME TOKEN')
        // console.log('$$$$ --- useTokenManager: checkTokenExpiration idToken VS user.idToken:', idToken != user.idToken ? 'DIFFERENT TOKEN' : 'SAME TOKEN')
    }
  }, [idToken, user.idToken, idTokenResultSave]);

  // reload user session
  const reloadUserSession = useCallback(async (currentUser) => {
    console.log('$$$ ---> useTokenManager: Starting user session reload...');
    try {
      const result = await reloadSession();
      
      if (result.data.error) {
        reactVerbosity >= 2 && console.log('$$$ ---> useTokenManager: Reload session error:', result.data.error);
        await invalidateUser();
        return false;
      } else {
        console.log('$$$ ---> useTokenManager: Reload session success');
        
        if (result.data !== '') {
          const userData = result.data;
          const accessToken = userData.accessToken;
          
          const verbosity = await fetchAppVerbosityApi(accessToken);
          console.log('$$$ ---> useTokenManager: Refresh user, verbosity:', userData.email, verbosity.data);
          
          // update all Redux state
          await dispatch(setAppVerbosity(verbosity.data.verbosity));
          await dispatch(setAppReactVerbosity(verbosity.data.reactVerbosity));
          await dispatch(setToken(accessToken));
          await dispatch(setIdToken(accessToken));
          await dispatch(setUser(JSON.stringify(userData)));
          await dispatch(setLoginStatus(userData.loggedIn));
          await dispatch(setRole(userData.role));
          await dispatch(setName(userData.name));
          await dispatch(setOrganization(userData.organization));
          
          if (userData && userData.organization && Object.keys(userData.organization).includes('privacy')) {
            await dispatch(setPrivacy(userData.organization.privacy));
          } else {
            await dispatch(setPrivacy(0));
          }
          const idTokenResult = await currentUser.getIdTokenResult(true);
          await dispatch(setExpiresAt(idTokenResult.expirationTime));
          
          console.log('$$$ ---> useTokenManager: User session reloaded successfully');
          return true;
        }
      }
    } catch (error) {
      console.error('$$$ ---> useTokenManager: Failed to reload user session:', error);
      await invalidateUser();
      return false;
    }
  }, [dispatch, reactVerbosity]);

  const invalidateUser = async () => {
    reactVerbosity === 5 && console.log("useTokenManager invalidateUser: Session expired. Please log in again.");
    await dispatch(unloadUnityApp(true));
    await dispatch(setSentTokenMessage(false));
    await dispatch(setLoginStatus(false));
    await dispatch(resetUser());
    await dispatch(setCurrentPage('login'));
    navigate('/login', { replace: true });
  };

  const logoutUser = useCallback(async () => {
    reactVerbosity === 5 && console.log('$$$$ --- useTokenManager: logoutUser ---- $$$$')
    await dispatch(unloadUnityApp(true));
    await dispatch(setLoginStatus(false));
    await dispatch(setSentTokenMessage(false));
    await dispatch(resetUser());
    await dispatch(clearToken());
    await auth.signOut();
    await dispatch(setCurrentPage('login'));
    navigate('/login', { state: { message: 'Your session has expired. Please login again.' } });
  }, [dispatch, navigate]);

  useEffect(() => {
    const unsubscribe = auth.onIdTokenChanged(async (fbUser) => {
        reactVerbosity === 5 && console.log('$$$$ ---- useTokenManager onIdTokenChanged:', idTokenResultSave && idTokenResultSave.token != idToken ? 'new token' : 'no change')
        reactVerbosity === 5 && console.log('$$$$ ---- useTokenManager onIdTokenChanged newIdToken:', idTokenResultSave && idTokenResultSave.token == null ? 'newIdToken null' : 'newIdToken not null')
        reactVerbosity === 5 && console.log('$$$$ ---- useTokenManager onIdTokenChanged user.idToken:', fbUser, user, user.idToken == null ? 'user.idToken null' : 'user.idToken not null')
        console.log('$$$$ ---- useTokenManager onIdTokenChanged user.idToken:', fbUser, user, user.idToken == null ? 'user.idToken null' : 'user.idToken not null')
        
        if (!fbUser) { 
          // fbUser is null when user is logged out
          console.log('$$$$ ---- useTokenManager onIdTokenChanged user logged out, logging out user')
          logoutUser();
        }
        else if (fbUser && (user.name === "" || user.idToken === "")) {
          // If Firebase user exists but Redux state not synced, reload complete user session
          console.log('$$$$ ---- useTokenManager: Firebase user exists but Redux not synced, reloading user session...');
          const reloadSuccess = await reloadUserSession(fbUser);
          
          if (!reloadSuccess) {
            // if reload fails, invalidate user
            console.error('$$$ ---> useTokenManager: Failed to sync user info');
              await invalidateUser();
          }
        }
    });
    return () => unsubscribe();
  }, [dispatch, logoutUser, reloadUserSession, user.name, user.idToken]);

  useEffect(() => {
    const checkTokenExpiration = setInterval(async () => {
      reactVerbosity === 5 && console.log('$$$$ ---- useTokenManager checkTokenExpiration')
      const user = auth.currentUser;
      reactVerbosity === 5 && console.log('$$$$ ---- useTokenManager checkTokenExpiration user:', user)
      
      if (user) {
        user.getIdTokenResult(true).then(async (idTokenResult) => {
          reactVerbosity === 5 &&   console.log('$$$$ --- useTokenManager: checkTokenExpiration idTokenResult:', idTokenResult)
          reactVerbosity === 5 &&   console.log('$$$$ --- useTokenManager: checkTokenExpiration result VS idToken:', idTokenResult.token != idToken ? 'DIFFERENT TOKEN' : 'SAME TOKEN')
          reactVerbosity === 5 &&   console.log('$$$$ --- useTokenManager: checkTokenExpiration result VS user.idToken:', idTokenResult.token != user.idToken ? 'DIFFERENT TOKEN' : 'SAME TOKEN')
          reactVerbosity === 5 &&   console.log('$$$$ --- useTokenManager: checkTokenExpiration idToken VS user.idToken:', idToken != user.idToken ? 'DIFFERENT TOKEN' : 'SAME TOKEN')
          
          if (idTokenResult.token != idToken) {
            console.log(new Date())
            reactVerbosity === 5 && console.log('$$$$ --- useTokenManager: DIFFERENT TOKENS UPDATING REDUX\n', idTokenResult.token, '\n', idToken)
            await dispatch(setIdToken(idTokenResult.token));
            await dispatch(setLoginStatus(true));
            await dispatch(setToken(idTokenResult.token));
            await setIdTokenResultSave(idTokenResult);
          } else if (idTokenResult.token === "" || idTokenResult.token === null) {
            console.log('$$$$ ---- useTokenManager checkTokenExpiration: INVALIDATING USER')
            await invalidateUser();
          } 
          const expirationTime = new Date(idTokenResult.expirationTime).getTime();
          const currentTime = new Date().getTime();
          const timeLeft = Math.floor((expirationTime - currentTime) / 1000 / 60); // Time left in minutes
          await dispatch(setExpiresAt(idTokenResult.expirationTime));
          // console.log(`$$ ---- useTokenManager: checkTokenExpiration Time left before token expiration: ${timeLeft} minutes`);
        });
      }
    }, 10 * 60 * 1000); // Check every (m) minutes m < n

    return () => clearInterval(checkTokenExpiration);
  }, []);
};